import React, { useState, useEffect, useCallback, useRef } from "react";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { Link } from "react-router-dom";
import { 
  Card, 
  Row, 
  Col, 
  Form, 
  InputGroup, 
  Button, 
  Spinner, 
  Alert, 
  Badge,
  Dropdown
} from "react-bootstrap";
import axios from "axios";
import {
  Search,
  GeoAlt,
  Map,
  ArrowLeft,
  Layers,
  ListUl,
  ExclamationCircle,
  CheckCircle,
  XCircle,
  Person,
  Building,
  Envelope,
  Phone,
  ZoomIn,
  ZoomOut,
  Fullscreen,
  Grid,
  PinMap,
  Globe,
  BrightnessHigh,
  Moon,
  Brush,
  MapFill
} from "react-bootstrap-icons";
import { API_BASE_URL } from "../../constants/api_constants";

// Import Leaflet styles
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Import des composants React Leaflet
import { MapContainer, TileLayer, Popup, CircleMarker, Tooltip, useMap } from 'react-leaflet';

// Styles personnalisés
const customStyles = `
  .stats-card {
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    height: 100%;
  }
  .stats-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 16px rgba(0,0,0,0.1) !important;
  }
  .stats-icon {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
  }
  .leaflet-popup-content-wrapper {
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
  .leaflet-popup-tip {
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
  .custom-marker {
    transition: all 0.2s ease-out;
    cursor: pointer;
  }
  .custom-marker:hover {
    filter: drop-shadow(0 0 4px rgba(0,0,0,0.3));
  }
  .leaflet-popup-content {
    margin: 12px;
  }
  @keyframes markerPopIn {
    0% { transform: scale(0); opacity: 0; }
    100% { transform: scale(1); opacity: 1; }
  }
  @media (max-width: 768px) {
    .stats-icon {
      width: 40px;
      height: 40px;
    }
    .stats-icon svg {
      width: 20px;
      height: 20px;
    }
  }
`;

// Injecter les styles
const styleSheet = document.createElement("style");
styleSheet.textContent = customStyles;
document.head.appendChild(styleSheet);

// Composant pour les contrôles de carte
const MapControls = () => {
  const map = useMap();
  
  return (
    <div className="leaflet-control-custom" style={{
      position: 'absolute',
      bottom: '20px',
      right: '20px',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    }}>
      <Button size="sm" variant="light" className="rounded-circle shadow-sm" onClick={() => map.zoomIn()} style={{ width: '36px', height: '36px' }}>
        <ZoomIn size={18} />
      </Button>
      <Button size="sm" variant="light" className="rounded-circle shadow-sm" onClick={() => map.zoomOut()} style={{ width: '36px', height: '36px' }}>
        <ZoomOut size={18} />
      </Button>
      <Button size="sm" variant="light" className="rounded-circle shadow-sm" onClick={() => map.setView([0.3901, 9.4544], 12)} style={{ width: '36px', height: '36px' }}>
        <PinMap size={18} />
      </Button>
      <Button size="sm" variant="light" className="rounded-circle shadow-sm" onClick={() => {
        const container = map.getContainer();
        if (!document.fullscreenElement) container.requestFullscreen();
        else document.exitFullscreen();
      }} style={{ width: '36px', height: '36px' }}>
        <Fullscreen size={18} />
      </Button>
    </div>
  );
};

// Point counter
const PointCounter = ({ count }) => {
  const map = useMap();
  const [visiblePoints, setVisiblePoints] = useState(count);
  
  useEffect(() => {
    const updateVisiblePoints = () => setVisiblePoints(count);
    map.on('moveend', updateVisiblePoints);
    map.on('zoomend', updateVisiblePoints);
    return () => {
      map.off('moveend', updateVisiblePoints);
      map.off('zoomend', updateVisiblePoints);
    };
  }, [map, count]);
  
  return (
    <div style={{
      position: 'absolute',
      top: '10px',
      right: '10px',
      zIndex: 1000,
      backgroundColor: 'white',
      borderRadius: '20px',
      padding: '6px 12px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
      fontSize: '12px',
      fontWeight: 'bold'
    }}>
      <GeoAlt size={14} className="text-primary me-1" />
      {visiblePoints} point(s)
    </div>
  );
};

// Composant qui gère le centrage sur un point spécifique
const MapCentering = ({ targetCenter, targetZoom, onCentered }) => {
  const map = useMap();
  
  useEffect(() => {
    if (targetCenter && targetCenter.length === 2) {
      map.flyTo(targetCenter, targetZoom || 15, {
        duration: 1.5,
        easeLinearity: 0.25
      });
      if (onCentered) onCentered();
    }
  }, [targetCenter, targetZoom, map, onCentered]);
  
  return null;
};

// Correction des icônes Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const TAXPAYERS_ENDPOINT = `${API_BASE_URL}/taxpayers`;

// Badge helpers
const getStatusBadge = (statut) => {
  const config = {
    actif: { label: "Actif", color: "success", icon: <CheckCircle className="me-1" size={12} /> },
    inactif: { label: "Inactif", color: "secondary", icon: <XCircle className="me-1" size={12} /> },
    en_retard: { label: "En retard", color: "danger", icon: <ExclamationCircle className="me-1" size={12} /> }
  };
  const cfg = config[statut] || { label: statut || "Actif", color: "success", icon: null };
  return <Badge bg={cfg.color} className="d-inline-flex align-items-center px-2 py-1">{cfg.icon}{cfg.label}</Badge>;
};

const getTypeBadge = (type) => {
  const config = {
    particulier: { label: "Particulier", color: "primary" },
    entreprise: { label: "Entreprise", color: "success" },
    commercant: { label: "Commerçant", color: "warning" },
    professionnel: { label: "Professionnel", color: "info" }
  };
  const cfg = config[type] || { label: "Entreprise", color: "secondary" };
  return <Badge bg={cfg.color} className="px-2 py-1">{cfg.label}</Badge>;
};

// Fonction pour obtenir la hiérarchie complète d'une localité
const getFullHierarchy = (localiteId, localitesMap) => {
  if (!localiteId || !localitesMap[localiteId]) return null;
  
  const hierarchy = [];
  let current = localitesMap[localiteId];
  
  // Parcourir la hiérarchie de bas en haut (du plus petit au plus grand)
  while (current) {
    hierarchy.push(current.nom);
    if (current.parent_id && localitesMap[current.parent_id]) {
      current = localitesMap[current.parent_id];
    } else {
      break;
    }
  }
  
  // Retourner dans l'ordre croissant (Quartier > Commune > Ville > Province)
  return hierarchy.join(' > ');
};

// Custom marker
const CustomMarker = ({ contribuable, localitesMap }) => {
  const getMarkerColor = (status) => {
    switch(status) {
      case 'actif': return '#28a745';
      case 'en_retard': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getMarkerSize = (status) => {
    switch(status) {
      case 'actif': return 10;
      case 'en_retard': return 12;
      default: return 8;
    }
  };

  // Obtenir l'adresse formatée avec hiérarchie complète
  const getFormattedAddress = () => {
    const parts = [];
    if (contribuable.adresse && contribuable.adresse !== "Non spécifiée" && contribuable.adresse !== "") {
      parts.push(contribuable.adresse);
    }
    if (contribuable.localite_id) {
      const hierarchy = getFullHierarchy(contribuable.localite_id, localitesMap);
      if (hierarchy) {
        parts.push(hierarchy);
      }
    }
    return parts.length > 0 ? parts.join(', ') : "Adresse non spécifiée";
  };

  const markerRef = useRef(null);
  
  useEffect(() => {
    if (markerRef.current) {
      const element = markerRef.current.getElement();
      if (element) element.style.animation = 'markerPopIn 0.3s ease-out';
    }
  }, []);
  
  return (
    <CircleMarker
      ref={markerRef}
      center={[contribuable.latitude, contribuable.longitude]}
      radius={getMarkerSize(contribuable.statut)}
      fillColor={getMarkerColor(contribuable.statut)}
      color="#fff"
      weight={2}
      opacity={1}
      fillOpacity={0.85}
      className="custom-marker"
      eventHandlers={{
        mouseover: (e) => e.target.openTooltip(),
        mouseout: (e) => e.target.closeTooltip(),
        click: (e) => e.target.openPopup()
      }}
    >
      <Tooltip sticky direction="top" offset={[0, -10]}>
        <div className="text-center">
          <strong>{contribuable.nom}</strong><br />
          <small className="text-muted">{contribuable.type}</small>
        </div>
      </Tooltip>
      <Popup minWidth={280} maxWidth={350}>
        <div className="p-2">
          <div className="d-flex align-items-center mb-3">
            <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-2">
              {contribuable.type === 'entreprise' ? <Building size={20} className="text-primary" /> : <Person size={20} className="text-primary" />}
            </div>
            <div>
              <h6 className="mb-0">{contribuable.nom}</h6>
              <small className="text-muted">ID: {contribuable.contribuable_id}</small>
            </div>
          </div>
          
          <div className="mb-2">
            {getStatusBadge(contribuable.statut)}
            <span className="ms-2">{getTypeBadge(contribuable.type)}</span>
          </div>
          
          <hr className="my-2" />
          
          <div className="small">
            <div className="d-flex align-items-center mb-2">
              <Phone size={12} className="text-muted me-2" />
              <span>{contribuable.telephone || 'Non renseigné'}</span>
            </div>
            <div className="d-flex align-items-center mb-2">
              <Envelope size={12} className="text-muted me-2" />
              <span className="text-truncate">{contribuable.email || 'Non renseigné'}</span>
            </div>
            <div className="d-flex align-items-start mb-2">
              <GeoAlt size={12} className="text-muted me-2 mt-1" />
              <span className="small">{getFormattedAddress()}</span>
            </div>
          </div>
          
          <hr className="my-2" />
          
          <Link to={`/agent/ContribuableDetails/${contribuable.contribuable_id}`} className="btn btn-sm btn-primary w-100 text-white">
            Voir détails
          </Link>
        </div>
      </Popup>
    </CircleMarker>
  );
};

export default function ContribuablesCartographie() {
  const [contribuables, setContribuables] = useState([]);
  const [filteredContribuables, setFilteredContribuables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [mapCenter, setMapCenter] = useState([0.3901, 9.4544]);
  const [mapZoom, setMapZoom] = useState(12);
  const [viewMode, setViewMode] = useState('map');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [mapStyle, setMapStyle] = useState('streets');
  const [stats, setStats] = useState({ total: 0, withCoordinates: 0, withoutCoordinates: 0 });
  const mapRef = useRef(null);
  
  // État pour le centrage sur un point spécifique
  const [centerTarget, setCenterTarget] = useState(null);
  const [centerZoom, setCenterZoom] = useState(null);
  
  // États pour les localités
  const [localites, setLocalites] = useState([]);
  const [loadingLocalites, setLoadingLocalites] = useState(false);
  const [localitesMap, setLocalitesMap] = useState({});

  const getAuthToken = () => sessionStorage.getItem("authToken");
  
  const getHeaders = () => {
    const token = getAuthToken();
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  };

  const getTileUrl = () => {
    switch(mapStyle) {
      case 'satellite':
        return 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
      case 'dark':
        return 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
      case 'light':
        return 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
      case 'outdoor':
        return 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png';
      default:
        return 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
    }
  };

  const getTileAttribution = () => {
    switch(mapStyle) {
      case 'satellite':
        return '&copy; <a href="https://www.esri.com">Esri</a> contributors';
      case 'dark':
      case 'light':
        return '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> & <a href="https://carto.com/">CARTO</a>';
      case 'outdoor':
        return '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> & <a href="https://opentopomap.org">OpenTopoMap</a>';
      default:
        return '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
    }
  };

  // Charger les localités depuis l'API
  const fetchLocalites = useCallback(async () => {
    try {
      setLoadingLocalites(true);
      const response = await axios.get(`${API_BASE_URL}/localites`, {
        headers: getHeaders()
      });
      
      let localitesData = [];
      if (response.data && Array.isArray(response.data)) {
        localitesData = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        localitesData = response.data.data;
      }
      
      // Créer un map pour accéder rapidement par localite_id
      const map = {};
      localitesData.forEach(loc => {
        map[loc.localite_id] = loc;
      });
      
      setLocalites(localitesData);
      setLocalitesMap(map);
    } catch (error) {
      console.error("Erreur chargement localités:", error);
    } finally {
      setLoadingLocalites(false);
    }
  }, []);

  const fetchContribuables = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${TAXPAYERS_ENDPOINT}/liste?per_page=9999&has_coordinates=true`, { headers: getHeaders() });
      
      if (response.data?.success) {
        const contribuablesData = response.data.data || [];
        const withCoordinates = contribuablesData.filter(c => c.latitude && c.longitude && !isNaN(parseFloat(c.latitude)));
        
        const formattedData = withCoordinates.map(item => ({
          id: item.id,
          contribuable_id: item.contribuable_id,
          nom: item.nom_contribuable,
          type: item.type || 'entreprise',
          email: item.email || item.admin_email || "",
          localite_id: item.localite_id,
          telephone: item.telephone || "",
          adresse: item.adresse || "",
          statut: item.statut || "actif",
          latitude: parseFloat(item.latitude),
          longitude: parseFloat(item.longitude)
        }));
        
        setContribuables(formattedData);
        setFilteredContribuables(formattedData);
        setStats({
          total: contribuablesData.length,
          withCoordinates: formattedData.length,
          withoutCoordinates: contribuablesData.length - formattedData.length
        });
        if (formattedData.length > 0) setMapCenter([formattedData[0].latitude, formattedData[0].longitude]);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { 
    fetchContribuables(); 
    fetchLocalites(); 
  }, [fetchContribuables, fetchLocalites]);

  useEffect(() => {
    let filtered = [...contribuables];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c => c.nom?.toLowerCase().includes(term) || c.contribuable_id?.toString().includes(term));
    }
    if (filterStatus !== 'all') filtered = filtered.filter(c => c.statut === filterStatus);
    if (filterType !== 'all') filtered = filtered.filter(c => c.type === filterType);
    setFilteredContribuables(filtered);
  }, [searchTerm, filterStatus, filterType, contribuables]);

  const fitBounds = () => {
    if (mapRef.current && filteredContribuables.length > 0) {
      const bounds = L.latLngBounds(filteredContribuables.map(c => [c.latitude, c.longitude]));
      mapRef.current.fitBounds(bounds, { padding: [50, 50] });
    }
  };

  // Fonction pour centrer sur un contribuable spécifique
  const centerOnContribuable = (contribuable) => {
    setViewMode('map');
    setCenterTarget([contribuable.latitude, contribuable.longitude]);
    setCenterZoom(15);
    // Réinitialiser après l'animation
    setTimeout(() => {
      setCenterTarget(null);
      setCenterZoom(null);
    }, 2000);
  };

  return (
    <React.Fragment>
      <Header />
      
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f5f7fa', minHeight: '100vh' }}>
        <div className="container-fluid">
          {/* En-tête */}
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
            <div>
              <ol className="breadcrumb mb-1">
                <li className="breadcrumb-item"><Link to="/dashboard">Tableau de bord</Link></li>
                <li className="breadcrumb-item"><Link to="/agent/contribuables/liste">Contribuables</Link></li>
                <li className="breadcrumb-item active">Cartographie</li>
              </ol>
              <h4 className="mb-0">Cartographie des Contribuables</h4>
              <p className="text-muted mb-0">Visualisation géographique interactive</p>
            </div>
            
            <div className="d-flex gap-2 flex-wrap">
              <Dropdown align="end">
                <Dropdown.Toggle variant="outline-secondary" size="sm">
                  <Map className="me-1" size={14} />
                  {mapStyle === 'streets' && 'Rues'}
                  {mapStyle === 'satellite' && 'Satellite'}
                  {mapStyle === 'light' && 'Clair'}
                  {mapStyle === 'dark' && 'Sombre'}
                  {mapStyle === 'outdoor' && 'Extérieur'}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setMapStyle('streets')}><Map size={14} className="me-2" /> Rues (OSM)</Dropdown.Item>
                  <Dropdown.Item onClick={() => setMapStyle('satellite')}><Globe size={14} className="me-2" /> Satellite (Esri)</Dropdown.Item>
                  <Dropdown.Item onClick={() => setMapStyle('light')}><BrightnessHigh size={14} className="me-2" /> Clair (CartoDB)</Dropdown.Item>
                  <Dropdown.Item onClick={() => setMapStyle('dark')}><Moon size={14} className="me-2" /> Sombre (CartoDB)</Dropdown.Item>
                  <Dropdown.Item onClick={() => setMapStyle('outdoor')}><PinMap size={14} className="me-2" /> Extérieur</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              
              <div className="btn-group btn-group-sm">
                <Button variant={viewMode === 'map' ? 'primary' : 'outline-primary'} onClick={() => setViewMode('map')}>
                  <Map size={14} className="me-1" /> Carte
                </Button>
                <Button variant={viewMode === 'list' ? 'primary' : 'outline-primary'} onClick={() => setViewMode('list')}>
                  <ListUl size={14} className="me-1" /> Liste
                </Button>
              </div>
              
              <Link to="/agent/contribuables/liste">
                <Button variant="outline-secondary" size="sm">
                  <ArrowLeft size={14} className="me-1" /> Retour
                </Button>
              </Link>
            </div>
          </div>

          {/* Statistiques */}
         <Row className="mb-4 g-4">
  <Col md={3} sm={6}>
    <Card className="border-0 shadow-sm stats-card h-100">
      <Card.Body className="p-3 p-md-4">
        <div className="d-flex align-items-center">
          <div className="flex-shrink-0">
            <div className="stats-icon-wrapper bg-primary bg-opacity-15 rounded-3 p-3 me-3">
              <Person size={28} className="text-white" />
            </div>
          </div>
          <div className="flex-grow-1">
            <h6 className="text-muted mb-1 small text-uppercase fw-semibold">Total contribuables</h6>
            <h3 className="mb-0 fw-bold display-7">{stats.total}</h3>
          </div>
        </div>
      </Card.Body>
    </Card>
  </Col>
  
  <Col md={3} sm={6}>
    <Card className="border-0 shadow-sm stats-card h-100">
      <Card.Body className="p-3 p-md-4">
        <div className="d-flex align-items-center">
          <div className="flex-shrink-0">
            <div className="stats-icon-wrapper bg-success bg-opacity-15 rounded-3 p-3 me-3">
              <GeoAlt size={28} className="text-white" />
            </div>
          </div>
          <div className="flex-grow-1">
            <h6 className="text-muted mb-1 small text-uppercase fw-semibold">Avec coordonnées</h6>
            <h3 className="mb-0 fw-bold display-7">{stats.withCoordinates}</h3>
            <div className="mt-1">
              <Badge bg="success" className="px-2 py-1 rounded-pill">
                <CheckCircle size={10} className="me-1" /> Localisés
              </Badge>
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  </Col>
  
  <Col md={3} sm={6}>
    <Card className="border-0 shadow-sm stats-card h-100">
      <Card.Body className="p-3 p-md-4">
        <div className="d-flex align-items-center">
          <div className="flex-shrink-0">
            <div className="stats-icon-wrapper bg-warning bg-opacity-15 rounded-3 p-3 me-3">
              <ExclamationCircle size={28} className="text-white" />
            </div>
          </div>
          <div className="flex-grow-1">
            <h6 className="text-muted mb-1 small text-uppercase fw-semibold">Sans coordonnées</h6>
            <h3 className="mb-0 fw-bold display-7">{stats.withoutCoordinates}</h3>
            <div className="mt-1">
              <Badge bg="warning" className="px-2 py-1 rounded-pill text-dark">
                <ExclamationCircle size={10} className="me-1" /> Non localisés
              </Badge>
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  </Col>
  
  <Col md={3} sm={6}>
    <Card className="border-0 shadow-sm stats-card h-100">
      <Card.Body className="p-3 p-md-4">
        <div className="d-flex align-items-center">
          <div className="flex-shrink-0">
            <div className="stats-icon-wrapper bg-info bg-opacity-15 rounded-3 p-3 me-3">
              <Layers size={28} className="text-white" />
            </div>
          </div>
          <div className="flex-grow-1">
            <h6 className="text-muted mb-1 small text-uppercase fw-semibold">Affichés sur carte</h6>
            <h3 className="mb-0 fw-bold display-7">{filteredContribuables.length}</h3>
            {filteredContribuables.length > 0 && (
              <Button 
                variant="link" 
                size="sm" 
                className="p-0 mt-1 text-info text-decoration-none"
                onClick={fitBounds}
              >
                <PinMap size={12} className="me-1" /> Centrer tout
              </Button>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  </Col>
</Row>

          {/* Filtres */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body>
              <Row className="g-2">
                <Col md={5}>
                  <InputGroup size="sm">
                    <InputGroup.Text><Search size={14} /></InputGroup.Text>
                    <Form.Control type="text" placeholder="Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                  </InputGroup>
                </Col>
                <Col md={3}>
                  <Form.Select size="sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="all">Tous les statuts</option>
                    <option value="actif">Actif</option>
                    <option value="inactif">Inactif</option>
                    <option value="en_retard">En retard</option>
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Select size="sm" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                    <option value="all">Tous les types</option>
                    <option value="particulier">Particulier</option>
                    <option value="entreprise">Entreprise</option>
                    <option value="commercant">Commerçant</option>
                    <option value="professionnel">Professionnel</option>
                  </Form.Select>
                </Col>
                <Col md={1}>
                  <Button size="sm" variant="outline-secondary" onClick={() => { setSearchTerm(''); setFilterStatus('all'); setFilterType('all'); }} className="w-100">
                    Reset
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Contenu principal */}
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /><p className="mt-3 text-muted">Chargement des données...</p></div>
          ) : error ? (
            <Alert variant="danger" className="text-center">
              <ExclamationCircle size={48} className="mb-3" />
              <h5>Erreur de chargement</h5>
              <p>{error}</p>
              <Button variant="primary" onClick={fetchContribuables}>Réessayer</Button>
            </Alert>
          ) : filteredContribuables.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center py-5">
                <GeoAlt size={64} className="text-muted mb-3" />
                <h5>Aucun contribuable avec coordonnées</h5>
                <p className="text-muted">
                  {stats.withCoordinates === 0 
                    ? "Aucun contribuable n'a de coordonnées GPS enregistrées."
                    : "Aucun résultat ne correspond à vos critères de recherche."}
                </p>
                {searchTerm && (
                  <Button variant="outline-primary" onClick={() => setSearchTerm('')}>
                    Effacer la recherche
                  </Button>
                )}
              </Card.Body>
            </Card>
          ) : viewMode === 'map' ? (
            <Card className="border-0 shadow-sm overflow-hidden">
              <Card.Body className="p-0" style={{ position: 'relative', height: '600px' }}>
                <MapContainer ref={mapRef} center={mapCenter} zoom={mapZoom} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true} zoomControl={false}>
                  <TileLayer attribution={getTileAttribution()} url={getTileUrl()} />
                  <MapCentering targetCenter={centerTarget} targetZoom={centerZoom} />
                  {filteredContribuables.map((c) => (
                    <CustomMarker
                      key={c.id}
                      contribuable={c}
                      localitesMap={localitesMap}
                    />
                  ))}
                  <MapControls />
                  <PointCounter count={filteredContribuables.length} />
                </MapContainer>
                <div style={{ position: 'absolute', bottom: '20px', left: '20px', zIndex: 1000, backgroundColor: 'rgba(255,255,255,0.9)', borderRadius: '8px', padding: '6px 12px', fontSize: '11px', backdropFilter: 'blur(4px)' }}>
                  <div className="d-flex gap-3 flex-wrap">
                    <div className="d-flex align-items-center"><div className="rounded-circle me-1" style={{ width: 10, height: 10, backgroundColor: '#28a745' }}></div><span>Actif</span></div>
                    <div className="d-flex align-items-center"><div className="rounded-circle me-1" style={{ width: 10, height: 10, backgroundColor: '#dc3545' }}></div><span>En retard</span></div>
                    <div className="d-flex align-items-center"><div className="rounded-circle me-1" style={{ width: 10, height: 10, backgroundColor: '#6c757d' }}></div><span>Inactif</span></div>
                    <div className="d-flex align-items-center"><div className="rounded-circle me-1" style={{ width: 10, height: 10, backgroundColor: '#ffc107' }}></div><span>Cliquer pour détails</span></div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          ) : (
            <Card className="border-0 shadow-sm">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th>Contribuable</th>
                      <th>Type</th>
                      <th>Téléphone</th>
                      <th>Statut</th>
                      <th>Localité</th>
                      <th>Coordonnées</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContribuables.map((c) => {
                      const localiteHierarchy = getFullHierarchy(c.localite_id, localitesMap);
                      const displayLocalite = localiteHierarchy || (c.localite_id && localitesMap[c.localite_id]?.nom) || "-";
                      return (
                        <tr key={c.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: 32, height: 32 }}>
                                {c.type === 'entreprise' ? <Building size={14} /> : <Person size={14} />}
                              </div>
                              <div>
                                <h6 className="mb-0">{c.nom}</h6>
                                <small className="text-muted">ID: {c.contribuable_id}</small>
                              </div>
                            </div>
                          </td>
                          <td>{getTypeBadge(c.type)}</td>
                          <td>{c.telephone}</td>
                          <td>{getStatusBadge(c.statut)}</td>
                          <td className="small text-muted" style={{ maxWidth: '200px' }}>{displayLocalite}</td>
                          <td><code className="small bg-light p-1 rounded">{c.latitude.toFixed(6)}, {c.longitude.toFixed(6)}</code></td>
                          <td className="text-end">
                            <div className="d-flex gap-2 justify-content-end">
                              <Button size="sm" variant="outline-primary" onClick={() => centerOnContribuable(c)}>
                                <PinMap size={12} className="me-1" />Centrer
                              </Button>
                              <Link to={`/agent/ContribuableDetails/${c.contribuable_id}`}>
                                <Button size="sm" variant="outline-secondary">Détails</Button>
                              </Link>
                            </div>
                           </td>
                         </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      </div>
    
    
<style>{`
    .stats-card {
  transition: all 0.3s ease;
  border-radius: 16px !important;
  overflow: hidden;
}

.stats-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0,0,0,0.1) !important;
}

.stats-icon-wrapper {
  transition: all 0.3s ease;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stats-card:hover .stats-icon-wrapper {
  transform: scale(1.05);
}

.display-6 {
  font-size: 1.75rem;
  font-weight: 700;
}

@media (max-width: 768px) {
  .stats-icon-wrapper {
    width: 48px;
    height: 48px;
  }
  .stats-icon-wrapper svg {
    width: 22px;
    height: 22px;
  }
  .display-6 {
    font-size: 1.5rem;
  }
}
  `}</style>
      <Footer />
    </React.Fragment>
  );
}